import { detail, fmtNum, laborHrs, money, parseNum } from '@/components/estimating/assemblies/assemblyFormat';
import { unitMoney } from '@/components/settings/estimating/materials/materialFormat';

export { detail, fmtNum, laborHrs, money, parseNum, unitMoney };

// Hours, short: "28 h", "98.9 h" (the builder's lines and the estimates list).
export const hoursShort = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} h`;

export const BUCKETS = [
  { key: 'material', label: 'Materials', short: 'Material' },
  { key: 'labor', label: 'Labor', short: 'Labor' },
  { key: 'equipment', label: 'Equipment', short: 'Equipment' },
  { key: 'subcontractor', label: 'Subcontractors', short: 'Sub' },
];
export const BUCKET_LABEL = Object.fromEntries(BUCKETS.map((b) => [b.key, b.short]));
// Margin status in the theme's status colors (strata-theme.css).
export const GAUGE = { on: 'var(--strata-success)', near: 'var(--strata-warning)', below: 'var(--strata-danger)', none: 'var(--strata-text-muted)' };
export const GAUGE_BG = { on: 'bg-[var(--strata-success)]', near: 'bg-[var(--strata-warning)]', below: 'bg-[var(--strata-danger)]', none: 'bg-[var(--strata-text-muted)]' };

export const pct1 = (n) => `${Number(n || 0).toFixed(1)}%`;
// GP can go below zero: "−$12.50" (never "$-12.50"), and a loss reads in the danger color.
export const gpMoney = (n) => (Number(n) < 0 ? `−${money(-Number(n))}` : money(n));
export const gpTone = (n) => (Number(n) < 0 ? 'text-[var(--strata-danger)]' : 'text-[#0D1B2A]');
export const hrs = (n) => `${fmtNum(n)} hr${Number(n) === 1 ? '' : 's'}`;
export const pts = (n) => `${n >= 0 ? '+' : '−'}${Math.abs(Number(n || 0)).toFixed(1)} pts`;
export const isStale = (e) => e?.response?.status === 409 && !!e.response.data?.stale;
export const isLocked = (e) => e?.response?.status === 409 && !!e.response.data?.locked;
export const STATUS = {
  draft: { label: 'Draft', cls: 'border-[#C89A3C]/50 bg-[#C89A3C]/10 text-[#8A6A22]' },
  ready: { label: 'Ready', cls: 'border-[#1F8A5B]/45 bg-[#1F8A5B]/10 text-[#17694A]' },
  archived: { label: 'Archived', cls: 'border-[#8A8F99]/50 bg-[#8A8F99]/10 text-[#5B606A]' },
  // Client picks (the company setting on): a Ready estimate sent for client picks, then with the client's picks in.
  waiting: { label: 'Waiting on client', cls: 'border-dashed border-amber-400 bg-amber-50 text-amber-800' },
  picks_in: { label: 'Picks in', cls: 'border-[#4575A8]/45 bg-[#4575A8]/10 text-[#2F5784]' },
};
export const PICKS_STATUS = { waiting: 'waiting', in: 'picks_in' };
export const dateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
export const findSection = (est, sid) => est?.sections.find((s) => s.id === sid);
export const findLine = (est, sid, lid) => findSection(est, sid)?.lines.find((l) => l.id === lid);
export const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
// A calendar day ("YYYY-MM-DD", no timezone shift) → "Oct 3, 2026"; today as YYYY-MM-DD on this device.
export const dayDate = (d) => {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};
export const todayISO = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
export const ASSEMBLY_RECENTS = 'strata.estimate.assemblyPicker.recents';
export const CLIENT_RECENTS = 'strata.estimate.clientPicker.recents';
